"use client";

import type { ComponentProps } from "react";
import Link from "next/link";
import { SignedIn, SignedOut, useAuth, useOrganization } from "@clerk/nextjs";
import { Button } from "~/components/ui/button";
import {
  marketingAppDashboardUrl,
  marketingClerkSignInAbsoluteUrl,
} from "~/lib/auth-paths";
import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";

function useHasActiveProjects() {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const orgId = organization?.id;

  const projectsQuery = api.project.getAllProjects.useQuery(
    { organisationId: orgId, status: "active" },
    {
      enabled: authLoaded && orgLoaded && isSignedIn && !!orgId,
      staleTime: 30_000,
    },
  );

  const hasProjects =
    projectsQuery.isSuccess && (projectsQuery.data?.length ?? 0) > 0;

  return {
    hasProjects,
    isChecking:
      isSignedIn &&
      (!authLoaded || !orgLoaded || (!!orgId && projectsQuery.isPending)),
  };
}

type PublicAuthNavButtonProps = {
  variant?: ComponentProps<typeof Button>["variant"];
  size?: ComponentProps<typeof Button>["size"];
  className?: string;
  buttonClassName?: string;
  fullWidth?: boolean;
  onNavigate?: () => void;
  /** Force `?brand=` on Sign In (e.g. siteglue on `/onboard`). */
  brandId?: "siteglue" | "inbound_gpt";
  signInTestId?: string;
  dashboardTestId?: string;
};

export function PublicAuthNavButton({
  variant = "ghost",
  size = "sm",
  className,
  buttonClassName,
  fullWidth = false,
  onNavigate,
  brandId,
  signInTestId = "link-sign-in",
  dashboardTestId = "link-dashboard",
}: PublicAuthNavButtonProps) {
  const { hasProjects, isChecking } = useHasActiveProjects();
  const buttonClasses = cn(
    "text-sm font-medium",
    fullWidth && "w-full",
    buttonClassName,
    className,
  );
  const signInHref = marketingClerkSignInAbsoluteUrl(
    brandId ? { brand: brandId } : undefined,
  );

  return (
    <>
      <SignedOut>
        <Link
          href={signInHref}
          onClick={onNavigate}
          data-testid={signInTestId}
        >
          <Button variant={variant} size={size} className={buttonClasses}>
            Sign In
          </Button>
        </Link>
      </SignedOut>
      <SignedIn>
        {!isChecking && hasProjects ? (
          <Link
            href={marketingAppDashboardUrl()}
            onClick={onNavigate}
            data-testid={dashboardTestId}
          >
            <Button variant={variant} size={size} className={buttonClasses}>
              Dashboard
            </Button>
          </Link>
        ) : null}
      </SignedIn>
    </>
  );
}

export function PublicAuthFooterLink({
  className,
  onNavigate,
}: {
  className?: string;
  onNavigate?: () => void;
}) {
  const { hasProjects, isChecking } = useHasActiveProjects();

  return (
    <>
      <SignedOut>
        <Link
          href={marketingClerkSignInAbsoluteUrl()}
          className={className}
          onClick={onNavigate}
        >
          Sign In
        </Link>
      </SignedOut>
      <SignedIn>
        {!isChecking && hasProjects ? (
          <Link href={marketingAppDashboardUrl()} className={className} onClick={onNavigate}>
            Dashboard
          </Link>
        ) : null}
      </SignedIn>
    </>
  );
}
